import { Client as BelfastClient } from '@kaizenplatform/belfast';
import { BucketId, FileReference, FileReferenceId } from '@kaizenplatform/belfast-entity';
import Store from 'repatch';

import State from '../core/State';
import ErrorPresenter from '../service/ErrorPresenter';
import MessageDispatcher from '../service/MessageDispatcher';
import DownloadApi from '../service/persistent/DownloadApi';
import FilesWebApi from '../service/persistent/FilesWebApi';
import ProjectChangeWebApi from '../service/persistent/ProjectChangeWebApi';
import ProjectWebApi, { ProjectNotFound, ProjectType } from '../service/persistent/ProjectWebApi';
import {
  dispose as disposeProject,
  finishFetchingProject,
  setAssetFiles,
  setBeforeBriefFiles,
  setBriefFiles,
  setLatestBriefFiles,
  setProject,
  setProjectChange,
  startFetchingProject,
} from '../thunk/project';
import { getAssetFiles, getBriefFileBucketIds, getBriefFiles } from '../utility/graphql/project';

class ShowProjectUsecase {
  private readonly store: Store<State>;
  private readonly belfastClient: BelfastClient;
  private readonly projectWebApi: ProjectWebApi;
  private readonly downloadApi: DownloadApi;
  private readonly errorPresenter: ErrorPresenter;
  private readonly messageDispatcher: MessageDispatcher;
  private readonly projectChangeWebApi: ProjectChangeWebApi;
  private readonly filesWebApi: FilesWebApi;
  private hugotApiBaseUrl: string;

  constructor({
    store,
    belfastClient,
    projectWebApi,
    downloadApi,
    errorPresenter,
    messageDispatcher,
    projectChangeWebApi,
    filesWebApi,
    hugotApiBaseUrl,
  }: {
    store: Store<State>;
    belfastClient: BelfastClient;
    projectWebApi: ProjectWebApi;
    downloadApi: DownloadApi;
    errorPresenter: ErrorPresenter;
    messageDispatcher: MessageDispatcher;
    projectChangeWebApi: ProjectChangeWebApi;
    filesWebApi: FilesWebApi;
    hugotApiBaseUrl: string;
  }) {
    this.store = store;
    this.belfastClient = belfastClient;
    this.projectWebApi = projectWebApi;
    this.downloadApi = downloadApi;
    this.errorPresenter = errorPresenter;
    this.messageDispatcher = messageDispatcher;
    this.projectChangeWebApi = projectChangeWebApi;
    this.filesWebApi = filesWebApi;
    this.hugotApiBaseUrl = hugotApiBaseUrl;
  }

  public prepare = async (projectId: string): Promise<void> => {
    this.store.dispatch(startFetchingProject());
    let project: ProjectType;
    const language = this.store.getState().language;
    try {
      project = await this.projectWebApi.getById(projectId, language);
    } catch (err) {
      if (err instanceof ProjectNotFound) {
        this.errorPresenter.showAsFatal(new Error('message.fetch_data_has_failed'));

        this.store.dispatch(finishFetchingProject());
        return;
      }
      this.store.dispatch(finishFetchingProject());
      throw err;
    }

    const briefFiles = await (async () => {
      const creativeBriefValue = project.creativeBriefValue;
      if (creativeBriefValue) {
        const briefFileBucketIds = getBriefFileBucketIds(
          project,
          (key) => creativeBriefValue.values.get(key) as string,
        );
        return await getBriefFiles(project, briefFileBucketIds, this.belfastClient, this.filesWebApi);
      } else {
        return new Map<string, FileReference[]>();
      }
    })();

    const assetFiles = await getAssetFiles(project, this.belfastClient, this.filesWebApi);
    this.store.dispatch(setAssetFiles(assetFiles));

    const projectChange = await this.projectChangeWebApi.getById(project);
    if (projectChange) {
      /**
       * Brief File取得
       */
      const latestVersion = projectChange.latestVersion;
      const latestBriefFiles = await (async () => {
        const creativeBriefValue = latestVersion.creativeBriefValue;
        if (creativeBriefValue) {
          const briefFileBucketIds = getBriefFileBucketIds(
            project,
            (key) => creativeBriefValue.values.get(key) as string,
          );
          return await getBriefFiles(project, briefFileBucketIds, this.belfastClient, this.filesWebApi);
        } else {
          return new Map<string, FileReference[]>();
        }
      })();
      this.store.dispatch(setLatestBriefFiles(latestBriefFiles));

      const beforeVersion = projectChange.beforeVersion;
      if (beforeVersion) {
        const beforeBriefFiles = await (async () => {
          const creativeBriefValue = beforeVersion.creativeBriefValue;
          if (creativeBriefValue) {
            const briefFileBucketIds = getBriefFileBucketIds(
              project,
              (key) => creativeBriefValue.values.get(key) as string,
            );
            return await getBriefFiles(project, briefFileBucketIds, this.belfastClient, this.filesWebApi);
          } else {
            return new Map<string, FileReference[]>();
          }
        })();
        this.store.dispatch(setBeforeBriefFiles(beforeBriefFiles));
      }

      this.store.dispatch(setProjectChange(projectChange));
    }
    this.store.dispatch(setBriefFiles(briefFiles));
    this.store.dispatch(setProject(project));
    this.store.dispatch(finishFetchingProject());
  };

  public downloadAssets = async ({
    buckets = [],
    files = [],
    projects = [],
  }: {
    buckets: ReadonlyArray<BucketId>;
    files: ReadonlyArray<FileReferenceId>;
    projects: ReadonlyArray<string>;
  }): Promise<void> => {
    const bucketIdForFiles = this.getBucketIdForDownloadAssetFiles();

    try {
      await this.downloadApi.download({
        buckets,
        files,
        projects,
        bucketIdForFiles,
        handler: (response: any) => {
          // 定期的に圧縮状態を確認するタイマーを設定します
          const timerId = setInterval(() => {
            const downloadKey = response.data.download_key;
            this.checkCompressionIsFinished({
              downloadKey: downloadKey,
              finishedHandler: () => {
                this.messageDispatcher.notifyUnimportant('project_basic_setting_detail.assets.download_all.compressed');
                this.downloadCompressedFile({ downloadKey: downloadKey });
                clearInterval(timerId);
              },
            });
          }, 1000);
        },
      });
      this.messageDispatcher.notifyUnimportant('project_basic_setting_detail.assets.download_all.succeeded');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    }
  };

  getBucketIdForDownloadAssetFiles = () => {
    // For Project
    const { project } = this.store.getState().project;
    if (project != null) {
      if (project.bucketId == null) {
        throw new Error('project.bucketId must not null.');
      }

      return project.bucketId;
    }

    throw new Error('<state>.project and <state>.offer must not null.');
  };

  public dispose = (): void => {
    this.store.dispatch(disposeProject());
  };

  private getDownload = async ({
    downloadKey,
    handler,
  }: {
    downloadKey: string;
    handler?: (response: any) => void;
  }) => {
    try {
      await this.downloadApi.get({ downloadKey, handler });
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    }
  };

  // 指定されたダウンロードキーのアイテムの圧縮処理が完了しているか否かを検証し、その結果を返却します
  private checkCompressionIsFinished = async ({
    downloadKey,
    finishedHandler,
  }: {
    downloadKey: string;
    finishedHandler: () => void;
  }) => {
    this.getDownload({
      downloadKey: downloadKey,
      handler: (response: any) => {
        if (response.data.status === 'finished_compression') {
          finishedHandler();
        }
      },
    });
  };

  private downloadCompressedFile = async ({ downloadKey }: { downloadKey: string }) => {
    try {
      this.messageDispatcher.notifyUnimportant('project_basic_setting_detail.assets.download_all.compressed');
      await this.downloadApi.downloadFile({ downloadKey: downloadKey, baseUrl: this.hugotApiBaseUrl });
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    }
  };

  public getFiles = async (
    project: Pick<
      ProjectType,
      | 'id'
      | 'bucketId'
      | 'creativeBriefDefinition'
      | 'creativeBriefValue'
      | 'assetCategoryDefinition'
      | 'assetCategoryValue'
    >,
  ) => {
    const briefFiles: Map<string, FileReference[]> = await (async () => {
      const creativeBriefValue = project.creativeBriefValue;
      if (creativeBriefValue) {
        const briefFileBucketIds = getBriefFileBucketIds(
          project,
          (key) => creativeBriefValue.values.get(key) as string,
        );
        return await getBriefFiles(project, briefFileBucketIds, this.belfastClient, this.filesWebApi);
      } else {
        return new Map<string, FileReference[]>();
      }
    })();
    const assetFiles: FileReference[] = await getAssetFiles(project, this.belfastClient, this.filesWebApi);
    return { briefFiles, assetFiles };
  };
}

export default ShowProjectUsecase;
